// Admin-only user picker. Loads /api/diUsers, shows a searchable dropdown,
// and calls startImpersonating(email) on selection.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { loadDiUsers } from '../auth/diUsersCache';
import './ImpersonatePicker.css';

export default function ImpersonatePicker() {
  const { isAdmin, impersonating, startImpersonating } = useAuth();
  const [users, setUsers]     = useState(null);   // null = not yet loaded
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const rootRef = useRef(null);

  // Lazy-load the user list the first time the picker opens. The shared
  // cache means even if this component remounts, the roster is fetched
  // only once per page load.
  useEffect(() => {
    if (!open || users !== null || loading) return;
    setLoading(true); setError(null);
    loadDiUsers()
      .then(list => setUsers(list))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, users, loading]);

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const s = search.trim().toLowerCase();
    if (!s) return users.slice(0, 100);
    return users
      .filter(u =>
        u.email.toLowerCase().includes(s) ||
        (u.name || '').toLowerCase().includes(s)
      )
      .slice(0, 100);
  }, [users, search]);

  if (!isAdmin) return null;

  function pick(email) {
    startImpersonating(email);
    setOpen(false);
  }

  return (
    <div className="imp-wrap" ref={rootRef}>
      <button
        className="imp-btn"
        onClick={() => setOpen(v => !v)}
        title={impersonating
          ? `Currently viewing as ${impersonating} — pick another user to switch`
          : 'View the app as another user'}
      >
        <i className="fas fa-user-secret" /> {impersonating ? 'Switch User…' : 'View As…'}
      </button>
      {open && (
        <div className="imp-dropdown">
          <input
            autoFocus
            className="imp-search"
            placeholder="Search name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="imp-list">
            {loading  && <div className="imp-empty">Loading users…</div>}
            {error    && <div className="imp-empty imp-err">Error: {error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div className="imp-empty">No matches</div>
            )}
            {filtered.map(u => (
              <button key={u.email} className="imp-item" onClick={() => pick(u.email)}>
                <span className="imp-item-name">{u.name}</span>
                <span className="imp-item-email">{u.email}</span>
                {u.roles?.length > 0 && (
                  <span className="imp-item-roles">{u.roles.join(', ')}</span>
                )}
              </button>
            ))}
            {users && users.length > 100 && !search && (
              <div className="imp-empty">Showing first 100 of {users.length}. Search to narrow.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
